/**
 * Hosts the public gallery will frame as a 3D tour. Measured 7 Oct 2026 over
 * every Legacy prod `zillow_3d_link` (the source Connect's `url` assets are
 * migrated from): 84,942 are https://www.zillow.com; the rest are free text
 * ("not completed at the time of appointment"), 4 Dropbox links and 2 Matterport
 * links from 2021. Add a host here only for a tour vendor we actually deliver.
 */
export const TOUR_HOSTS: readonly string[] = ['www.zillow.com'];

/**
 * Returns the URL if it is an absolute https URL on an allowlisted tour host,
 * else null.
 *
 * The 3D tour link is the stored `url` of a `zillow_3d_link` asset, which
 * asset-service copies from caller input with no scheme check. Rendering it
 * raw as an iframe `src` or anchor `href` would let a `javascript:` (or
 * `data:`) value run in this origin against every visitor of the share link,
 * and any other https host would put an arbitrary site inside a
 * PlanOmatic-branded public page (a phishing surface). `new URL()` rejects
 * relative and malformed values; the protocol and host checks reject the rest.
 */
export const safeEmbedUrl = (value: string | null | undefined): string | null => {
  const url = parseHttps(value);
  return url !== null && TOUR_HOSTS.includes(url.hostname) ? url.href : null;
};

/**
 * Returns the URL if it is an absolute https URL, else null. For the account's
 * logo and website (DV-3713), which asset-service passes through as whatever
 * the account has on file: any host is expected, but never a `javascript:` or
 * `data:` value, nor a relative path that would resolve against this origin.
 */
export const safeHttpsUrl = (value: string | null | undefined): string | null =>
  parseHttps(value)?.href ?? null;

const parseHttps = (value: string | null | undefined): URL | null => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
};
